import React from 'react';
import { Container, Title, Text, Stack, Divider, Card, Anchor } from '@mantine/core';

const HelpPage: React.FC = () => {
  return (
    <Container size="md" style={{ marginTop: '40px', marginBottom: '40px' }}>
      <Stack gap="xl">
        <div>
          <Title order={1}>Hilfe - TimberTrack IoT Platform</Title>
          <Text size="lg" c="dimmed" mt="sm">
            Willkommen bei der TimberTrack IoT Device Control Platform
          </Text>
        </div>

        <Divider />

        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Title order={2} mb="md">Was ist TimberTrack?</Title>
          <Text>
            TimberTrack ist eine professionelle IoT-Plattform zur Überwachung und Steuerung von Sensoren und Gateways. 
            Die Plattform ermöglicht es Ihnen, Messdaten in Echtzeit zu erfassen, zu visualisieren und zu analysieren. 
            Mit modernster TimescaleDB-Technologie und Keycloak-Authentifizierung bietet TimberTrack höchste 
            Sicherheit und Performance für Ihre IoT-Daten.
          </Text>
        </Card>

        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Title order={2} mb="md">Hauptfunktionen</Title>
          <Stack gap="md">
            <div>
              <Text fw={600}>📊 Zusammenfassung (Dashboard)</Text>
              <Text size="sm" c="dimmed">
                Übersicht über alle Ihre Geräte und deren aktuelle Messwerte. Hier sehen Sie auf einen Blick 
                den Status Ihrer Sensoren, Batteriestand, Inaktivitätsstatus und die wichtigsten Metriken 
                mit interaktiven Diagrammen und Filtermöglichkeiten.
              </Text>
            </div>

            <div>
              <Text fw={600}>📈 Daten</Text>
              <Text size="sm" c="dimmed">
                Detaillierte Visualisierung von Messdaten über Zeit. Sie können verschiedene Metriken auswählen, 
                Zeiträume filtern, Daten exportieren und erweiterte Analysen durchführen.
              </Text>
            </div>

            <div>
              <Text fw={600}>📋 Messwerte</Text>
              <Text size="sm" c="dimmed">
                Tabellarische Ansicht aller erfassten Messwerte. Hier können Sie nach bestimmten Sensoren, 
                Zeiträumen oder Metriken filtern und die Daten für weitere Analysen exportieren.
              </Text>
            </div>

            <div>
              <Text fw={600}>🔌 Verbinden</Text>
              <Text size="sm" c="dimmed">
                Geräteverbindungsmanagement mit Bluetooth und USB-Unterstützung. Hier können Sie neue 
                Gateways und Sensoren hinzufügen, konfigurieren und deren Verbindungsstatus überwachen. 
                Entwicklermodus für erweiterte Konfigurationen verfügbar.
              </Text>
            </div>

            <div>
              <Text fw={600}>📡 Gateway</Text>
              <Text size="sm" c="dimmed">
                Detaillierte Ansicht und Konfiguration Ihrer Gateways. Gateways sind die Brücke zwischen 
                Ihren Sensoren und der Cloud-Plattform mit vollständiger Konfigurationskontrolle.
              </Text>
            </div>

            <div>
              <Text fw={600}>🌡️ Sensor</Text>
              <Text size="sm" c="dimmed">
                Verwaltung einzelner Sensoren mit erweiterten Funktionen. Hier können Sie Sensoren konfigurieren, 
                Schwellenwerte setzen, historische Daten einsehen und Sensordaten analysieren.
              </Text>
            </div>

            <div>
              <Text fw={600}>📱 Gerät</Text>
              <Text size="sm" c="dimmed">
                Zentralisierte Geräteübersicht mit kombinierten Gateway- und Sensorinformationen. 
                Optimiert für mobile Ansicht und schnellen Zugriff auf Gerätestatus.
              </Text>
            </div>

            <div>
              <Text fw={600}>⚙️ Einstellungen</Text>
              <Text size="sm" c="dimmed">
                Personalisierungsoptionen inklusive Theme-Wechsel (Hell/Dunkel), Sprachauswahl, 
                Schwellenwertkonfiguration, Wetter-Integration und Testmodus.
              </Text>
            </div>
          </Stack>
        </Card>

        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Title order={2} mb="md">Administration (nur für Admins)</Title>
          <Stack gap="md">
            <div>
              <Text fw={600}>📦 Assets</Text>
              <Text size="sm" c="dimmed">
                Verwaltung aller Hardware-Assets (Gateways und Sensoren). Hier können Sie neue Geräte registrieren, 
                MAC-Adressen zuordnen, Gerätetypen festlegen und Asset-Lebenszyklen verwalten.
              </Text>
            </div>

            <div>
              <Text fw={600}>🏢 Kunden</Text>
              <Text size="sm" c="dimmed">
                Verwaltung von Kundenkonten mit Multi-Tenant-Unterstützung. Jeder Kunde hat Zugriff 
                auf seine eigenen Geräte und Daten mit vollständiger Datentrennung.
              </Text>
            </div>

            <div>
              <Text fw={600}>👥 Benutzer</Text>
              <Text size="sm" c="dimmed">
                Benutzerverwaltung mit Rollenzuweisung (Admin, User, Viewer). Integration mit Keycloak 
                für sichere Authentifizierung und Berechtigungsverwaltung.
              </Text>
            </div>
          </Stack>
        </Card>

        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Title order={2} mb="md">Erste Schritte</Title>
          <Stack gap="sm">
            <Text>
              <strong>1. Anmelden:</strong> Melden Sie sich mit Ihren Keycloak-Zugangsdaten an.
            </Text>
            <Text>
              <strong>2. Zusammenfassung ansehen:</strong> Nach dem Login sehen Sie die Übersicht Ihrer Geräte und deren Status.
            </Text>
            <Text>
              <strong>3. Geräte verbinden:</strong> Gehen Sie zu &quot;Verbinden&quot; um neue Gateways oder Sensoren hinzuzufügen.
            </Text>
            <Text>
              <strong>4. Daten analysieren:</strong> Nutzen Sie die &quot;Daten&quot; und &quot;Messwerte&quot; Seiten zur detaillierten Analyse.
            </Text>
            <Text>
              <strong>5. Einstellungen anpassen:</strong> Konfigurieren Sie Theme, Sprache, Schwellenwerte und weitere Optionen.
            </Text>
            <Text>
              <strong>6. Administration (falls zutreffend):</strong> Verwalten Sie Assets, Kunden und Benutzer über die Admin-Funktionen.
            </Text>
          </Stack>
        </Card>

        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Title order={2} mb="md">Unterstützte Metriken & Einheiten</Title>
          <Stack gap="sm">
            <Text>
              <strong>Temperatur:</strong> Gemessen in °C (Grad Celsius) mit hoher Präzision
            </Text>
            <Text>
              <strong>Luftfeuchtigkeit:</strong> Gemessen in % (Prozent) mit Kalibrierungsoptionen
            </Text>
            <Text>
              <strong>Luftdruck:</strong> Gemessen in hPa (Hektopascal) für Wetterüberwachung
            </Text>
            <Text>
              <strong>Batteriestatus:</strong> Gemessen in % (Prozent) und V (Volt) mit Ladezustandsüberwachung
            </Text>
            <Text>
              <strong>Signalstärke:</strong> RSSI-Werte für Funkverbindungsqualität
            </Text>
            <Text>
              <strong>Aktivitätsstatus:</strong> Zeit seit letzter Kommunikation mit automatischer Inaktivitätserkennung
            </Text>
          </Stack>
        </Card>

        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Title order={2} mb="md">Technologie & Sicherheit</Title>
          <Text>
            <strong>Backend:</strong> Python FastAPI mit TimescaleDB für hochperformante Zeitreihen-Daten
          </Text>
          <Text>
            <strong>Frontend:</strong> React mit TypeScript, Mantine UI und ECharts für Visualisierungen
          </Text>
          <Text>
            <strong>Authentifizierung:</strong> Keycloak mit OAuth 2.0 und JWT-Tokens für höchste Sicherheit
          </Text>
          <Text>
            <strong>Datenschutz:</strong> Multi-Tenant-Architektur mit vollständiger Datentrennung zwischen Kunden
          </Text>
          <Text>
            <strong>SSL/TLS:</strong> Verschlüsselte Kommunikation mit Zertifikaten von timbertrack.de
          </Text>
        </Card>

        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Title order={2} mb="md">Mobile Unterstützung</Title>
          <Text>
            Die Plattform ist vollständig responsiv und optimiert für mobile Geräte. 
            Alle Funktionen sind auf Smartphones und Tablets verfügbar mit Touch-optimierter Bedienung.
          </Text>
        </Card>

        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Title order={2} mb="md">Support & Kontakt</Title>
          <Text>
            Bei Fragen oder Problemen wenden Sie sich bitte an:
          </Text>
          <Text mt="sm">
            <strong>E-Mail:</strong> <Anchor href="mailto:office@timbertrack.de">office@timbertrack.de</Anchor>
          </Text>
          <Text>
            <strong>Telefon:</strong> 0179-1183141
          </Text>
          <Text mt="sm">
            <strong>Weitere Informationen:</strong><br />
            • <Anchor href="/imprint">Impressum</Anchor><br />
            • <Anchor href="/privacy">Datenschutz</Anchor><br />
            • <Anchor href="/terms">Haftungsausschluss</Anchor>
          </Text>
        </Card>

        <Divider />

        <Text size="sm" c="dimmed" ta="center">
          TimberTrack IoT Platform - Professionelle IoT-Lösung für Holzfeuchtemessung<br />
          © Galek GmbH • Am Brunnenbichl 2 • 82398 Polling
        </Text>
      </Stack>
    </Container>
  );
};

export default HelpPage;
